import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
// Multiple dialogs can overlap: only the last close restores the page.
let scrollLocks = 0;
let restoreScroll: (() => void) | undefined;
function lockBackgroundScroll() {
  if (scrollLocks++ === 0) {
    const root = document.documentElement;
    const body = document.body;
    const x = window.scrollX,
      y = window.scrollY;
    const gap = Math.max(0, window.innerWidth - root.clientWidth);
    const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
    const patches: [HTMLElement, string, string][] = [
      [root, 'overflow', 'hidden'],
      [body, 'position', 'fixed'],
      [body, 'top', `-${y}px`],
      [body, 'left', `-${x}px`],
      [body, 'width', '100%'],
      [body, 'overflow', 'hidden'],
      [body, 'padding-right', `${padding + gap}px`],
    ];
    const previous = patches.map(
      ([el, prop]) =>
        [el, prop, el.style.getPropertyValue(prop), el.style.getPropertyPriority(prop)] as const,
    );
    patches.forEach(([el, prop, value]) => el.style.setProperty(prop, value));
    restoreScroll = () => {
      previous.forEach(([el, prop, value, priority]) => {
        if (value) el.style.setProperty(prop, value, priority);
        else el.style.removeProperty(prop);
      });
      window.scrollTo({ left: x, top: y, behavior: 'instant' });
    };
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--scrollLocks === 0) {
      restoreScroll?.();
      restoreScroll = undefined;
    }
  };
}

export function Modal({
  title,
  onClose,
  children,
  wide = false,
  className = '',
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null),
    close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const el = ref.current;
    el?.showModal();
    const unlock = lockBackgroundScroll();
    const handler = (e: Event) => {
      e.preventDefault();
      close.current();
    };
    el?.addEventListener('cancel', handler);
    return () => {
      el?.removeEventListener('cancel', handler);
      el?.close();
      unlock();
    };
  }, []);
  return createPortal(
    <dialog
      ref={ref}
      className={`modal-dialog ${wide ? 'wide' : ''} ${className}`}
      aria-label={title}
    >
      <div className="modal-heading">
        <h2>{title}</h2>
        <button className="icon-button" onClick={onClose} aria-label="关闭">
          <X size={21} />
        </button>
      </div>
      {children}
    </dialog>,
    document.body,
  );
}
